import { useSelector } from 'react-redux';
import useGrowthBookFeatureValue from 'components/GrowthBookProvider/useGrowthBookFeatureValue';
import useWhitelabelEnvs from 'hooks/whitelabel/useWhitelabelEnvs';
import useSeatsBus from 'hooks/store/useSeatsBus';
import {
  purchaseMatchesShowPricesByGroupAllowedRoutes,
  resolveShowPricesByGroupConfig,
} from 'utils/showPricesByGroup';
import hasPrivilegedSeats from 'utils/privilegedSeatDetection';

/**
 * Encapsulates the "show prices by group" behaviour for the seat selection flow.
 * Gates evaluated in order:
 *   1. USE_SEAT_BASED_PRICING (whitelabel) — master kill switch.
 *   2. show_prices_by_group (GrowthBook) — master kill switch.
 *   3a. Privileged Seat bypass — if any seat in the layout has rgb_color,
 *       skip the route allowlist (IAMSA does not need per-route gating).
 *   3b. showPricesByGroupConfig route allowlist — used by tenants that roll
 *       out per-route (e.g. Flota Magdalena's "Price by Seat").
 *
 * Pure route logic lives in {@link utils/showPricesByGroup}.
 *
 * @returns {boolean} True when flags + tenant rules allow the feature.
 */
const useShowPricesByGroup = () => {
  const { features } = useSelector((state) => state.whitelabelConfig);
  const { showPricesByGroupConfig } = useWhitelabelEnvs();
  const purchase = useSelector((state) => state.purchase?.toJS?.() || {});
  const search = useSelector((state) => state.search);
  const seatsBus = useSeatsBus();
  const showPricesByGroupGB = useGrowthBookFeatureValue('show_prices_by_group');

  const flagsOn = Boolean(features.USE_SEAT_BASED_PRICING) && Boolean(showPricesByGroupGB);
  const resolved = resolveShowPricesByGroupConfig(showPricesByGroupConfig);

  const departsLayout = purchase?.departs?.layout;
  const returnsLayout = purchase?.returns?.layout;
  const seatsBusLayouts = Object.values(seatsBus || {})
    .map((entry) => entry?.layout)
    .filter(Boolean);
  const isPrivilegedSeat =
    hasPrivilegedSeats(departsLayout) ||
    hasPrivilegedSeats(returnsLayout) ||
    seatsBusLayouts.some((layout) => hasPrivilegedSeats(layout));

  let result;
  if (!flagsOn) {
    result = false;
  } else if (isPrivilegedSeat) {
    result = true;
  } else if (resolved === false) {
    result = false;
  } else if (resolved === true) {
    result = true;
  } else {
    result = purchaseMatchesShowPricesByGroupAllowedRoutes(purchase, search, resolved);
  }

  return result;
};

export default useShowPricesByGroup;
